import { DEFAULT_LIMIT, IMetricMethodologySettingsUI, OkrMethodologyFacade } from "./../../../goals/components/facades/okr-methodology.facade";
import { RequireAtLeastOne } from "./../../../models";
import { IAngularEvent, IPromise, IQService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { IPlanningSession } from "@gtmhub/sessions/models/models";
import { IGoal } from "@gtmhub/goals/models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { UIErrorHandlingService, IUIError } from "@gtmhub/error-handling";
import { IUnitFormattingForm } from "@gtmhub/shared/controllers/unit-formatting";
import { IInsightSelectorData } from "@gtmhub/insights";
import { MetricFormEventType, IPartialMetric, IMetricLinks, ICheckin, IMetric, MetricTargetOperator } from "../models/models";
import { metricProperties, MetricService } from "../services/metricService";
import { CfMap } from "@gtmhub/customFields";
import { MetricDeadLine, disableDatesOutsideSessionTimeframe, metricFormStrings, setMetricDueDates, restoreMetricDueDates } from "@gtmhub/okrs/metrics/utils";
import { IStateInit } from "@gtmhub/core/routing";
import { storage } from "@gtmhub/core/storage";
import dayjs from "@gtmhub/dayjs";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { getCurrentUserId } from "@gtmhub/users";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management/models";
import { AutoFieldsService, IMetricPrediction } from "@gtmhub/shared/services/auto-fields";
import { generateMetricPredictionAnalytics } from "@gtmhub/shared/utils/auto-fields-util";
import { WarningModalService } from "@gtmhub/shared/components/warning-modal/warning-modal.component";
import { AnalyticsService, Flavor, recommendation } from "@gtmhub/core/analytics";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { OkrEvents } from "@gtmhub/okrs";

export interface IMetricFormParams {
  planningSessionId: string;
  metricId: string;
  id: string;
}

interface IMetricFormReduxScope {
  sessionIdMap: Record<string, IPlanningSession>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

type MetricPredictedFields = "target" | "targetOperator" | "format" | "initialValue";

const bindStateToScope = (state: ISessionsStoreState): IMetricFormReduxScope => {
  return {
    sessionIdMap: state.sessions.map,
    reduxItemsFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error,
  };
};

export interface IMetricFormScope extends IModalScope, IMetricFormReduxScope, IExtendedRootScopeService {
  session: IPlanningSession;
  metric: IMetric;
  metricsLimitReached: boolean;
  allowedActions: string[];
  dueDatePicker: {
    minDate: dayjs.Dayjs;
    maxDate: dayjs.Dayjs;
  };
  dates: {
    dueDate: string;
    softDueDate: string;
  };
  softDueDatePicker: {
    minDate: dayjs.Dayjs;
    maxDate: dayjs.Dayjs;
  };
  useDeadline: MetricDeadLine;
  goal: IGoal;
  isEdit: boolean;
  originalMetricType: string;
  errorMessage: string;
  indicators: {
    loading: boolean;
    saving: boolean;
  };
  isFieldRequired: boolean;
  insightSelector: {
    data: IInsightSelectorData;
  };
  requiredCfNotEmpty: boolean;
  prediction: IMetricPrediction;
  shouldPredict: { [key: string]: boolean };
  metricMethodologySettings: IMetricMethodologySettingsUI & { metricsPerGoalLimit: number };

  onInsightSelectorChange(): void;

  save(): void;

  validateForm(metric: IMetric): boolean;

  validateDoubleInputValues(manualType: string, targetOperator: string, initialValue: number, targetValue: number, criticalValue: number): boolean;

  includeCritical(useCritical: boolean): boolean;

  insertCustomFormat(): void;

  selectCustomOption(option: string): void;

  setRequiredField(field: boolean): void;

  updateCustomFields(customFields: CfMap): void;

  dynamicKrsFeatureEnabled(): boolean;

  shouldDisplayInsightSelector(): boolean;

  setDeadline(deadline: MetricDeadLine): void;

  setDueDate(dueDate: string, type: "softDeadline" | "hardDeadline"): void;

  setRequiredCf(requiredCf: { areFilled: boolean }): void;

  selectMultipleOwners(ids: string[]): void;

  setAutopopulatedFields(metricTitle: string): void;

  updateMetricField(value: RequireAtLeastOne<IMetric>): void;

  edit(): void;

  isFieldPartOfWorkflow(filedName: string): boolean;
}

// We don't want the saved scope to survive page refresh
let cachedNewMetric: IMetric = null;
const cachedExistingMetricMap: Map<string, IMetric> = new Map();
let currentMetricId: string = null;
let cacheTimestamp = Date.now();
const isCacheTimedout = (): boolean => {
  const cacheTimeout = 5 * 60 * 1000; // 5 minuts in ms
  return Date.now() - cacheTimestamp > cacheTimeout;
};

export class MetricFormCtrl implements IStateInit {
  public static $inject = [
    "$q",
    "$rootScope",
    "$scope",
    "$stateParams",
    "GoalService",
    "MetricService",
    "PermissionsService",
    "UIErrorHandlingService",
    "UnitFormattingForm",
    "$ngRedux",
    "PlanningSessionsActions",
    "AutoFieldsService",
    "WarningModalService",
    "AnalyticsService",
    "FeatureToggleV2Service",
    "OkrMethodologyFacade",
  ];

  private okrEvents: OkrEvents;
  private isNumberFormattingFeatureEnabled: boolean;
  private isDeadlineChanged: boolean;

  constructor(
    private $q: IQService,
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IMetricFormScope,
    private $stateParams: IMetricFormParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private unitFormattingForm: IUnitFormattingForm,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private autoFieldsService: AutoFieldsService,
    private warningModalService: WarningModalService,
    private analyticsService: AnalyticsService,
    private featureToggleV2Service: FeatureToggleV2Service,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {
    this.okrEvents = new OkrEvents(this.$scope);

    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));

    this.$scope.save = this.save;
    this.$scope.validateForm = (metric: IMetric): boolean => {
      const isValidInputValues = this.validateDoubleInputValues(metric.manualType, metric.targetOperator, metric.initialValue, metric.target, metric.critical);

      return !isValidInputValues || !this.$scope.requiredCfNotEmpty;
    };

    this.$scope.selectMultipleOwners = this.selectMultipleOwners;

    this.$scope.metricsLimitReached = false;
    this.$scope.isEdit = false;
    this.$scope.indicators = {
      loading: true,
      saving: false,
    };
    this.$rootScope.ui = {
      maskGrid: true,
    };
    this.$scope.shouldPredict = {
      format: true,
      targetOperator: true,
      target: true,
      // will be set to 0 in webapp if target is predicted
      // and operator is not should decrease to
      initialValue: true,
    };

    this.$scope.dates = {
      dueDate: null,
      softDueDate: null,
    };
    this.isDeadlineChanged = false;

    this.$scope.selectCustomOption = this.selectCustomOption;
    this.$scope.setRequiredField = this.setRequiredField;
    // custom validation for KRs with manual type "double"
    this.$scope.validateDoubleInputValues = this.validateDoubleInputValues;
    // method used by boolen slider adjusting metric.critical and useCritical variable in view
    this.$scope.includeCritical = this.includeCritical;
    this.$scope.insertCustomFormat = this.insertCustomFormat;
    this.$scope.onInsightSelectorChange = this.onInsightSelectorChange;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.dynamicKrsFeatureEnabled = this.dynamicKrsFeatureEnabled;
    this.$scope.shouldDisplayInsightSelector = this.shouldDisplayInsightSelector;
    this.$scope.setDeadline = this.setDeadline;
    this.$scope.setDueDate = this.setDueDate;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.setAutopopulatedFields = this.setAutopopulatedFields;
    this.$scope.updateMetricField = this.updateMetricField;
    this.$scope.edit = this.edit;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);

    this.okrEvents = new OkrEvents(this.$scope);
  }

  stateInit(): IPromise<unknown> {
    return this.init();
  }

  private isFieldPartOfWorkflow(fieldName: string): boolean {
    return this.metricService.isFieldPartOfWorkflow(fieldName, this.$scope.session);
  }

  private originalMetric: IMetric;
  private selectedCustomFieldOptions: string[] = [];

  private selectCustomOption = (option: string): void => {
    if (this.selectedCustomFieldOptions.indexOf(option) !== -1) {
      this.selectedCustomFieldOptions.splice(this.selectedCustomFieldOptions.indexOf(option), 1);
    } else {
      this.selectedCustomFieldOptions.push(option);
    }

    if (this.selectedCustomFieldOptions.length > 0) {
      this.$scope.isFieldRequired = false;
    }

    if (this.selectedCustomFieldOptions.length < 1) {
      this.$scope.isFieldRequired = true;
    }
  };

  private setRequiredField = (field: boolean): void => {
    if (field) {
      this.$scope.isFieldRequired = true;
    } else {
      this.$scope.isFieldRequired = false;
    }
  };

  private onExit(event: IAngularEvent): void {
    // We want to save the state of the modal only if it was closed with backdrop
    // We consider this as accidental form close and we want to save the state of the form
    const modalCloseReason = String(storage.get("modalCloseReason"));
    if (currentMetricId) {
      if (modalCloseReason && modalCloseReason.includes("backdrop")) {
        cacheTimestamp = Date.now();
        cachedExistingMetricMap[currentMetricId] = (event.targetScope as IMetricFormScope).metric;
      } else {
        cachedExistingMetricMap[currentMetricId] = null;
      }
    } else {
      if (modalCloseReason && modalCloseReason.includes("backdrop")) {
        cacheTimestamp = Date.now();
        cachedNewMetric = (event.targetScope as IMetricFormScope).metric;
      } else {
        cachedNewMetric = null;
      }
    }
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators.loading = true;
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    // splitting initial load into 2 groups of requests
    // 2nd group to get planning session and allowed action may need goal id

    const goalParams = {
      fields: "private,id,sessionId,ownerIds,locked,workflow{status},metrics{id}",
    };

    this.$scope.$on("$destroy", this.onExit);

    const goalsAndMetricPromise = this.$q
      .all({
        goal: this.$stateParams.id ? this.goalService.getGoal(this.$stateParams.id, goalParams) : null,
        metric: this.$stateParams.metricId ? this.metricService.getMetric(this.$stateParams.metricId) : null,
        isNumberFormattingFeatureEnabled: this.featureToggleV2Service.isFeatureAvailable(FeatureFlag.NumberFormatting),
      })
      .then(
        ({ goal, metric, isNumberFormattingFeatureEnabled }) => {
          this.isNumberFormattingFeatureEnabled = isNumberFormattingFeatureEnabled;

          if (this.$stateParams.id) {
            this.$scope.goal = goal;
          }

          currentMetricId = this.$stateParams.metricId;

          // if editing a metric
          if (this.$stateParams.metricId) {
            if (cachedExistingMetricMap[currentMetricId] && !isCacheTimedout()) {
              this.$scope.metric = cachedExistingMetricMap[currentMetricId];
            } else {
              this.$scope.metric = metric;
            }

            this.setEditMetricData(this.$scope.metric);
          } else {
            // if creating a new metric
            if (cachedNewMetric && !isCacheTimedout()) {
              // Load metric from local variable
              this.$scope.metric = cachedNewMetric;
            } else {
              this.setNewMetricDetails();
            }
          }

          if (this.$scope.goal) {
            // basic case
            return this.loadSessionAndAllowedActions();
          } else {
            // in case if you open edit form and refresh
            return this.goalService.getGoal(this.$scope.metric.goalId, goalParams).then((goal) => {
              this.$scope.goal = goal;
              return this.loadSessionAndAllowedActions();
            });
          }
        },
        (err) => this.handleIndicatorError(err, "loading", false)
      );

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => goalsAndMetricPromise,
        () => this.uiErrorHandlingService.handleModal(this.$scope.reduxItemsError)
      );
  };

  private loadSessionAndAllowedActions(): IPromise<unknown> {
    return this.$q
      .all({
        allowedActions: this.$stateParams.metricId
          ? this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$scope.metric.id, "metric")
          : this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$scope.goal.id, "goal"),
        trackMetricForm: this.metricService.trackMetricForm(MetricFormEventType.KR_FORM_OPENED, this.$scope.metric),
      })
      .then(({ allowedActions }) => {
        this.$scope.indicators.loading = false;

        this.$scope.session = this.$scope.goal.sessionId && this.$scope.goal.sessionId !== "" ? this.$scope.sessionIdMap[this.$scope.goal.sessionId] : null;
        this.$scope.allowedActions = allowedActions.currentUserActions;

        disableDatesOutsideSessionTimeframe(this.$scope, this.$stateParams);
        this.setMetricMethodologySettings();
        this.checkMetricsLimit();

        if (this.shouldDisplayInsightSelector()) {
          this.$scope.insightSelector = { data: this.metricService.generateInsightSelectorData(this.$scope.metric) };
        }

        this.$scope.$broadcast("focusTextInput");
      });
  }

  private setMetricMethodologySettings(): void {
    this.$scope.metricMethodologySettings = {
      ...this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.$scope.session),
      metricsPerGoalLimit: this.okrMethodologyFacade.getLimitSetting("metric", this.$scope.session?.settings),
    };
  }

  private setEditMetricData = (metric: IMetric) => {
    if (metric.dueDate === metricFormStrings.default.date) {
      this.$scope.useDeadline = "none";
    }

    // needed when editing metric, originalMetricType is used in the view
    this.$scope.originalMetricType = metric.manualType;

    // needed when editing metric to send only edited fields via patch metric http request (if fields are objects, they are always sent with patch)
    this.originalMetric = angular.copy(metric);
    this.$scope.isEdit = true;
  };

  private setNewMetricDetails = () => {
    this.$scope.useDeadline = "none";

    this.$scope.metric = {
      ownerIds: this.$scope.goal.ownerIds,
      goalId: this.$stateParams.id,
      manualType: metricFormStrings.double,
      targetOperator: "at_least",
      initialValue: null,
      target: null,
      // setting critical to null when creating a new metric
      critical: null,
      name: null,
      dueDate: null,
      softDueDate: null,
      links: <IMetricLinks>{},
      customFields: {},
      format: {
        prefix: "",
        suffix: "",
        fractionSize: 0,
      },
      insightName: "",
    };
  };

  private checkMetricsLimit = (): void => {
    const metricsCount = this.$scope.goal.metrics?.length || -1;

    if (this.$scope.isEdit) {
      this.$scope.metricMethodologySettings.metricsPerGoalLimit = DEFAULT_LIMIT;
    }

    this.$scope.metricsLimitReached = metricsCount >= this.$scope.metricMethodologySettings.metricsPerGoalLimit;
  };

  private setDueDate = (dueDate: string, type: "softDeadline" | "hardDeadline"): void => {
    switch (type) {
      case "softDeadline":
        this.$scope.dates.softDueDate = dueDate;
        break;
      case "hardDeadline":
        this.$scope.dates.dueDate = dueDate;
        break;
    }
  };

  private setDeadline = (deadline: MetricDeadLine): void => {
    this.$scope.useDeadline = deadline;
    this.isDeadlineChanged = true;
  };

  private edit = () => {
    if (this.shouldOpenDeadlineWarningModal()) {
      this.warningModalService
        .warningModal({
          modalImage: "/dist/img/sandclock.svg",
          modalTitle: "are_you_sure_you_want_to_change_the_deadline",
          modalText: "changing_the_deadline_of_cascading_key_result",
          confirmText: "change",
          dismissText: "cancel",
        })
        .then(
          () => {
            this.save();
          },
          () => {
            restoreMetricDueDates(this.$scope);
          }
        );
    } else {
      this.save();
    }
  };

  private shouldOpenDeadlineWarningModal = (): boolean => {
    return (
      this.isCascadingMetric() &&
      (this.areDatesChanged(this.$scope.metric.dueDate, this.$scope.dates.dueDate) ||
        this.areDatesChanged(this.$scope.metric.softDueDate, this.$scope.dates.softDueDate) ||
        this.isDeadlineChanged)
    );
  };

  private isCascadingMetric = (): boolean => {
    return !!this.$scope.metric.cascadeType.length;
  };

  private areDatesChanged = (metricDueDate: string, scopeDueDate: string): boolean => {
    const areDueDatesDifferent: boolean = metricDueDate !== scopeDueDate;
    return (
      (areDueDatesDifferent && metricDueDate === metricFormStrings.default.date && this.isDeadlineChanged) ||
      (areDueDatesDifferent && metricDueDate !== metricFormStrings.default.date)
    );
  };

  private save = () => {
    this.$scope.indicators.saving = true;

    setMetricDueDates(this.$scope);

    // setting metric.critical as null if not creating anti-target KR
    this.setMetricCriticalTarget(this.$scope.metric);

    if (!this.$scope.metric.ownerIds) {
      this.$scope.metric.ownerIds = this.$scope.goal.ownerIds;
    }

    if (this.$scope.isEdit) {
      this.$rootScope.traceAction("update_KR", () => {
        this.updateMetric();
      });
    } else {
      this.$rootScope.traceAction("create_KR", () => {
        // setting new boolean initial to 0, target to 1, targetOperator to "at_least", prefix and suffix to ""
        this.setBooleanValues(this.$scope.metric.manualType, "at_least", 0, 1);

        if (this.$scope.metric.actual === undefined) {
          this.$scope.metric.actual = this.$scope.metric.initialValue;
        }
        const shouldCheckin = this.$scope.metric.actual !== this.$scope.metric.initialValue;
        this.metricService.createMetric(this.$scope.metric).then(
          (metric) => {
            if (shouldCheckin) {
              const checkin: ICheckin = {
                checkInDate: new Date().toISOString(),
                actual: this.$scope.metric.actual,
                confidence: this.$scope.metric.confidence?.value,
                changedBy: getCurrentUserId(),
                customFields: this.$scope.metric.customFields,
              };
              this.metricService.checkin(metric, checkin).then(() => {
                this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
                  checkin,
                  metric: this.$scope.metric,
                  goal: this.$scope.goal,
                  session: this.$scope.session,
                });
              });
            }

            if (this.$scope.prediction) {
              this.sendPredictionAnalyticsData(metric);
            }
            this.trackRecommendationHelpfulness(metric);
            this.afterSave();
          },
          (err) => this.handleIndicatorError(err, "saving", true)
        );
      });
    }
  };

  private trackRecommendationHelpfulness(metric: IMetric): void {
    //recommendation is helpful if user doesn’t remove themselves as owner
    this.analyticsService.track(recommendation, {
      is_useful: metric.ownerIds.includes(getCurrentUserId()),
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_metric;owner",
    });
  }

  private updateMetric = () => {
    // if metric type is changed
    if (this.$scope.originalMetricType !== this.$scope.metric.manualType) {
      // if changing type from number to boolean -> initial to 0, target to 1, targetOperator to "at_least", prefix and suffix to ""
      this.setBooleanValues(this.$scope.metric.manualType, "at_least", 0, 1);

      // setting actual value to initial value if manual type is changed
      // this logic could be removed when backend has implemented such logic when patching metric
      this.$scope.metric.actual = this.$scope.metric.initialValue;

      // else if metric type was not changed
      // but initial value is changed
    } else {
      // when editing number KRs if not checked in yet
      // if changing initial value -> change the actual value as well
      if (
        (this.$scope.metric.manualType === metricFormStrings.double || this.$scope.metric.manualType === metricFormStrings.empty) &&
        this.$scope.metric.metricHistory.length === 0 &&
        this.originalMetric.initialValue !== this.$scope.metric.initialValue
      ) {
        // making actual value equal to the new initial value
        this.$scope.metric.actual = this.$scope.metric.initialValue;
      }
    }

    // patching KR changes
    this.metricService.patchMetric(this.createPartialMetric(this.$scope.metric)).then(
      () => {
        this.afterSave();
      },
      (err) => this.handleIndicatorError(err, "saving", true)
    );
  };

  private createPartialMetric = (metric: IMetric): IPartialMetric => {
    const partialMetric: IPartialMetric = {
      id: metric.id,
    };

    let properties = metricProperties;
    if (this.$scope.goal.locked && this.$scope.session.workflow?.editableKrFields?.length > 0) {
      properties = this.metricService.filterWorkflowSelectionProperties(this.$scope.session.workflow.editableKrFields);
    }

    properties.forEach((property) => {
      if (metric[property] !== this.originalMetric[property]) {
        partialMetric[property] = metric[property];
      }
    });

    return partialMetric;
  };

  // dealing with boolean stuff
  // and adjusting targetOperator if changing type to boolean from number
  // boolean targetOperator for boolean metrics is always "at_least"
  private setBooleanValues = (manualType: string, targetOperator: MetricTargetOperator, initialValue: number, target: number) => {
    if (manualType === metricFormStrings.boolean) {
      this.$scope.metric.initialValue = initialValue;
      this.$scope.metric.target = target;

      if (this.$scope.metric.format) {
        this.$scope.metric.format.prefix = "";
        this.$scope.metric.format.suffix = "";
      }

      // boolean metrics targetOperator is always "at_least"
      this.$scope.metric.targetOperator = targetOperator ? targetOperator : "at_least";
    }
  };

  private setMetricCriticalTarget = (metric: IMetric): void => {
    if (
      (metric.manualType === metricFormStrings.double || metric.manualType === metricFormStrings.empty) &&
      (metric.targetOperator === "should_stay_above" || metric.targetOperator === "should_stay_below")
    ) {
      return;
    }

    // if metric is not anti-target KR --> set critical value to null
    this.$scope.metric.critical = null;
  };

  private afterSave = () => {
    this.$scope.indicators.saving = false;

    this.okrEvents.broadcastGoalsChanged("metricUpdated", {
      metric: this.$scope.metric,
      goal: this.$scope.goal,
      session: this.$scope.sessionIdMap[this.$scope.goal.sessionId],
    });

    // displaying goal and goals changes
    this.okrEvents.broadcastGoalsChanged("goalChanged");
    this.$rootScope.$broadcast("goalsChanged");

    this.$scope.$dismiss();
  };

  // ui validator not allowing to set "negative" attainment metrics
  private validateDoubleInputValues = (
    manualType: string,
    targetOperator: MetricTargetOperator,
    initialValue: number,
    targetValue: number,
    criticalValue: number
  ): boolean => {
    const isManualTypeDoubleOrEmpty = manualType === metricFormStrings.double || manualType === metricFormStrings.empty ? true : false;

    if (isManualTypeDoubleOrEmpty && (initialValue === null || targetValue === null)) {
      this.$scope.errorMessage = null;

      return false;
    }

    if (isManualTypeDoubleOrEmpty && targetOperator === "at_least" && initialValue >= targetValue) {
      this.$scope.errorMessage = "initial_lower_than_target";

      return false;
    }

    if (isManualTypeDoubleOrEmpty && targetOperator === "at_most" && initialValue <= targetValue) {
      this.$scope.errorMessage = "initial_greater_than_target";

      return false;
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === "should_stay_above" &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue >= targetValue
    ) {
      this.$scope.errorMessage = "critical_lower_than_target";

      return false;
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === "should_stay_below" &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue <= targetValue
    ) {
      this.$scope.errorMessage = "critical_greater_than_target";

      return false;
    }

    return true;
  };

  private includeCritical = (useCritical: boolean): boolean => {
    if (useCritical === true) {
      // setting metric.critical to be null when critical target is not used
      this.$scope.metric.critical = null;

      return false;
    } else {
      // ng-init in this case is set by the attributes of the input field
      return true;
    }
  };

  private insertCustomFormat = (): void => {
    if (!this.$scope.metric.format) {
      this.$scope.metric.format = {
        prefix: "",
        suffix: "",
        fractionSize: 0,
      };
    }

    this.unitFormattingForm.open({ formatting: this.$scope.metric.format }).then(() => {
      this.disablePredictionForField("format");
    });
  };

  private onInsightSelectorChange = (): void => {
    this.metricService.updateMetricInsightData(this.$scope.metric, this.$scope.insightSelector.data, this.isNumberFormattingFeatureEnabled, this.$scope.shouldPredict);
  };

  private handleIndicatorError = <E>(err: E, indicator: keyof IMetricFormScope["indicators"], inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private updateCustomFields = (customFields: CfMap) => {
    this.$scope.metric.customFields = customFields;
  };

  private dynamicKrsFeatureEnabled = (): boolean => this.$rootScope.featureAvailable("insightboards.dynamic-kr");

  private shouldDisplayInsightSelector = (): boolean =>
    this.dynamicKrsFeatureEnabled() && (this.$rootScope.hasPermission(metricFormStrings.manageData) || this.$rootScope.hasRole(metricFormStrings.admin));

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;
  };

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.metric.ownerIds = ids;
  };

  private setAutopopulatedFields = (metricTitle: string): void => {
    const noFieldsShouldBePredicted = Object.values(this.$scope.shouldPredict).every((v) => !v);
    if (noFieldsShouldBePredicted || this.$scope.metric.targetOperator === "boolean") {
      return;
    }

    this.autoFieldsService
      .getPredictionFromMetric(metricTitle)
      .then((prediction) => {
        this.$scope.prediction = prediction;
        this.updateAllPredictedMetrics(prediction);
      })
      .catch(() => {
        this.$scope.prediction = null;
      });
  };

  private updateAllPredictedMetrics = (prediction: IMetricPrediction): void => {
    for (const [field, shouldPredict] of Object.entries(this.$scope.shouldPredict)) {
      if (shouldPredict) {
        this.$scope.metric[field] = prediction[field] || null;
      }
    }

    const shouldPredictInitialValue =
      this.$scope.shouldPredict.target && this.$scope.shouldPredict.initialValue && this.$scope.metric.targetOperator !== "at_most" && prediction.target;

    if (shouldPredictInitialValue) {
      this.$scope.prediction.initialValue = 0;
      this.$scope.metric.initialValue = 0;
    }
  };

  private updateMetricField = (updatedField: RequireAtLeastOne<IMetric>) => {
    this.$scope.metric = { ...this.$scope.metric, ...updatedField };

    const predictedKey = Object.keys(updatedField).find((k) => ["targetOperator", "format", "target", "initialValue"].includes(k)) as MetricPredictedFields;
    if (predictedKey) {
      this.disablePredictionForField(predictedKey);
    }
  };

  private disablePredictionForField = (field: MetricPredictedFields) => {
    this.$scope.shouldPredict[field] = false;
  };

  private sendPredictionAnalyticsData = (newMetric: IMetric): void => {
    const data = generateMetricPredictionAnalytics(newMetric, this.$scope.prediction);
    this.autoFieldsService.sendMetricAnalyticsData(data);
  };
}
